import { FC, useCallback } from 'react';
import { Col, Row } from 'react-grid-system';
import styled from 'styled-components';
import {
  NormalFormSelectionField,
  NormalFormTextField,
  NormalFormCountrySelectionField,
  NormalAutoCompleteSearchSelectionField,
} from '../molecules';
import { Button } from '../atoms';
import {
  PropertyFilterInput,
  PropertyTypeOption,
  UnityStatusOption,
  OrderDirectionOption,
  PageSizeOption,
  useGetAllStatesLazyQuery,
  useGetAllCitiesLazyQuery,
} from '@/graphql/generated';
import { useUserLanguage } from '@/customHooks';
import { propertyTypeMapper, unityStatusOptionMapper, orderDirectionMapper, pageSizeMapper } from '@/helpers';

export type PropertyFiltersProps = {
  filters: PropertyFilterInput;
  onFilterChange: (newFilters: PropertyFilterInput) => void;
};

const textsData = {
  es: {
    countryLabel: 'País',
    countryPlaceholder: 'Buscar país',
    stateLabel: 'Estado / Provincia',
    statePlaceholder: 'Buscar estado',
    cityLabel: 'Ciudad',
    cityPlaceholder: 'Buscar ciudad',
  },
  en: {
    countryLabel: 'Country',
    countryPlaceholder: 'Search country',
    stateLabel: 'State / Province',
    statePlaceholder: 'Search state',
    cityLabel: 'City',
    cityPlaceholder: 'Search city',
  },
} as const;

const FiltersWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.light};
  padding: 1.5rem;
  border-radius: ${({ theme }) => theme.border.radius.lg};
  margin-bottom: 2rem;
`;

const ButtonCol = styled(Col)`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.md};

  @media (min-width: 992px) {
    margin-top: 0;
  }
`;

const initialPropertyFilters: PropertyFilterInput = {
  type: null,
  occupancyStatus: null,
  countryId: null,
  stateId: null,
  cityId: null,
  balcony: null,
  elevator: null,
  patio: null,
  kitchen: null,
  commonBathroom: null,
  commonAirConditioner: null,
  commonFan: null,
  orderDirection: OrderDirectionOption.Desc,
  search: null,
  pageSize: PageSizeOption.TwentyFive,
  page: null,
};

export const PropertyFilters: FC<PropertyFiltersProps> = ({ filters, onFilterChange }) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;

  const [getStates, { data: statesData }] = useGetAllStatesLazyQuery();
  const [getCities, { data: citiesData }] = useGetAllCitiesLazyQuery();

  const handleFieldChange = useCallback((field: keyof PropertyFilterInput, value: string | boolean | null) => {
    onFilterChange({
      ...filters,
      [field]: value === '' ? null : value,
    });
  }, [filters, onFilterChange]);

  const typeOptions = Object.values(PropertyTypeOption).map(value => ({
    name: propertyTypeMapper(value, userLanguage),
    value: value,
  }));

  const occupancyStatusOptions = Object.values(UnityStatusOption).map(value => ({
    name: unityStatusOptionMapper(value, userLanguage),
    value: value,
  }));

  const orderDirectionOptions = Object.values(OrderDirectionOption).map(value => ({
    name: orderDirectionMapper(value, userLanguage),
    value: value,
  }));

  const pageSizeOptions = [
    PageSizeOption.TwentyFive,
    PageSizeOption.Fifty,
    PageSizeOption.SeventyFive,
    PageSizeOption.OneHundred,
  ].map(value => ({
    name: pageSizeMapper(value, userLanguage),
    value: value,
  }));

  const stateOptions =
    statesData?.states.map((state) => ({
      name: state.name,
      value: state.geonamesAdmin1Code,
    })) ?? [];

  const cityOptions =
    citiesData?.cities.map((city) => ({
      name: city.name,
      value: city.geonamesId,
    })) ?? [];

  const handleCountryChange = useCallback((value: string | null) => {
    const newCountryId = value || null;
    const newFilters: PropertyFilterInput = {
      ...filters,
      countryId: newCountryId,
      stateId: null,
      cityId: null,
    };
    onFilterChange(newFilters);

    if (newCountryId) {
      getStates({ variables: { countryCode: newCountryId } });
    }
  }, [filters, getStates, onFilterChange]);

  const handleStateChange = useCallback((value: string | null) => {
    const newStateId = value === '' ? null : value;
    onFilterChange({
      ...filters,
      stateId: newStateId,
      cityId: null,
    });
    if (newStateId) {
      getCities({ variables: { stateCode: newStateId } });
    }
  }, [filters, getCities, onFilterChange]);

  const handleCityChange = useCallback((value: string | null) => {
    handleFieldChange('cityId', value);
  }, [handleFieldChange]);

  return (
    <FiltersWrapper>
      <Row gutterWidth={16} align="center">
        <Col md={4} sm={12}>
          <NormalFormTextField
            label="Buscar"
            name="search"
            placeholder="Buscar propiedades..."
            value={filters.search || ''}
            onChange={(value) => handleFieldChange('search', value)}
          />
        </Col>
        <Col md={3} sm={6}>
          <NormalFormSelectionField<PropertyTypeOption>
            label="Tipo de Propiedad"
            name="type"
            options={typeOptions}
            value={filters.type}
            onChange={(value) => handleFieldChange('type', value)}
            noValueText="Todos"
          />
        </Col>
        <Col md={3} sm={6}>
          <NormalFormSelectionField<UnityStatusOption>
            label="Estado de Ocupación"
            name="occupancyStatus"
            options={occupancyStatusOptions}
            value={filters.occupancyStatus}
            onChange={(value) => handleFieldChange('occupancyStatus', value)}
            noValueText="Todos"
          />
        </Col>
        <Col md={2} sm={6}>
          <NormalFormSelectionField<OrderDirectionOption>
            label="Orden"
            name="orderDirection"
            options={orderDirectionOptions}
            value={filters.orderDirection}
            onChange={(value) => handleFieldChange('orderDirection', value)}
          />
        </Col>
      </Row>
      <Row gutterWidth={16} align="center">
        <Col md={3} sm={6}>
          <NormalFormCountrySelectionField
            label={texts.countryLabel}
            name="countryId"
            value={filters.countryId ?? undefined}
            onChange={handleCountryChange}
            noValueText="Todos"
          />
        </Col>
        <Col md={3} sm={6}>
          <NormalAutoCompleteSearchSelectionField
            label={texts.stateLabel}
            name="stateId"
            placeholder={texts.statePlaceholder}
            value={filters.stateId ?? undefined}
            onChange={handleStateChange}
            options={stateOptions}
          />
        </Col>
        <Col md={3} sm={6}>
          <NormalAutoCompleteSearchSelectionField
            label={texts.cityLabel}
            name="cityId"
            placeholder={texts.cityPlaceholder}
            value={filters.cityId ?? undefined}
            onChange={handleCityChange}
            options={cityOptions}
          />
        </Col>
        <Col md={2} sm={6}>
          <NormalFormSelectionField<PageSizeOption>
            label="Registros por página"
            name="pageSize"
            options={pageSizeOptions}
            value={filters.pageSize}
            onChange={(value) => handleFieldChange('pageSize', value)}
            noValueText="Todos"
          />
        </Col>
        <ButtonCol md={4} sm={12}>
          <Button secondary onClick={() => onFilterChange(initialPropertyFilters)}>
            Limpiar Filtros
          </Button>
        </ButtonCol>
      </Row>
    </FiltersWrapper>
  );
};